import { useTranslation } from "react-i18next";
import { Clock, FileStack, Inbox, WifiOff } from "lucide-react";
import { ActionCard } from "@/features/dashboard/components/ActionCard";
import type { ActionCardTone } from "@/features/dashboard/components/ActionCard";
import type {
  ActionCardData,
  ActionCardKind,
} from "@/features/dashboard/lib/panelData";

/** What the dispatcher is asked to do about each kind of problem. */
export interface ActionCardHandlers {
  onUnassigned: () => void;
  onLate: () => void;
  onOffline: () => void;
  onDrafts: () => void;
}

interface Props {
  cards: ActionCardData[];
  handlers: ActionCardHandlers;
  /**
   * Unit label per kind, resolved by the caller for the card's count (counts
   * differ in unit, and the unit agrees with the number — BUG-719).
   */
  units: Record<ActionCardKind, (count: number) => string>;
  /** Per-kind reason the action cannot run, when it cannot. */
  disabledReasons?: Partial<Record<ActionCardKind, string>>;
}

const CARD_META: Record<
  ActionCardKind,
  { icon: typeof Inbox; tone: ActionCardTone }
> = {
  // «Не распределено» is the day's biggest backlog and the most common action,
  // so the SURFACE carries the urgency (a danger tint) while the BUTTON stays
  // `brand-primary`. A destructive-red button here — as the mockup draws it —
  // would train hesitation on the action a dispatcher takes every morning, and
  // «Собрать маршруты» creates work rather than destroying anything.
  unassigned: { icon: Inbox, tone: "danger" },
  late: { icon: Clock, tone: "warning" },
  offline: { icon: WifiOff, tone: "warning" },
  // Drafts are pending work, not a fault — ordinary brand, no alarm colour.
  drafts: { icon: FileStack, tone: "brand" },
};

/**
 * «ТРЕБУЕТ РЕШЕНИЯ» — the work queue that replaces the old scoreboard.
 *
 * Renders NOTHING (not the heading, not the badge, not a placeholder) when
 * `cards` is empty. That is the hard acceptance criterion: an "всё в порядке"
 * panel would re-create exactly the always-present chrome this redesign
 * removes. The caller passes an empty array both while loading and when every
 * count is a measured zero — a skeleton that resolves to zero cards is a flash
 * of four phantom problems.
 *
 * The badge counts CARDS SHOWN, never the sum of their counts.
 */
export function ActionBand({ cards, handlers, units, disabledReasons }: Props) {
  const { t } = useTranslation();

  if (cards.length === 0) return null;

  const onAction: Record<ActionCardKind, () => void> = {
    unassigned: handlers.onUnassigned,
    late: handlers.onLate,
    offline: handlers.onOffline,
    drafts: handlers.onDrafts,
  };

  return (
    <section aria-labelledby="action-band-heading">
      <div className="mb-2 flex items-center gap-2">
        <h2
          id="action-band-heading"
          className="text-[11px] font-semibold uppercase tracking-widest text-gray-400"
        >
          {t("dashboard.actionBand.title")}
        </h2>
        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-danger px-1.5 text-[11px] font-bold text-white">
          {cards.length}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const meta = CARD_META[card.kind];
          return (
            /* DYNAMIC i18n KEYS below — `dashboard.actionBand.${kind}.title`
               and `.action`. `check:i18n` scans static literals only and is
               blind to these (DEV_RULES §10.9), so the eight keys are listed
               here and pinned by a rendered-text test (ActionBand.test.tsx):
                 dashboard.actionBand.unassigned.title / .action
                 dashboard.actionBand.late.title       / .action
                 dashboard.actionBand.offline.title    / .action
                 dashboard.actionBand.drafts.title     / .action */
            <ActionCard
              key={card.kind}
              icon={meta.icon}
              tone={meta.tone}
              title={t(`dashboard.actionBand.${card.kind}.title`)}
              count={card.count}
              unit={units[card.kind](card.count)}
              detail={card.detail}
              actionLabel={t(`dashboard.actionBand.${card.kind}.action`)}
              onAction={onAction[card.kind]}
              actionDisabledReason={disabledReasons?.[card.kind]}
            />
          );
        })}
      </div>
    </section>
  );
}
